export type PlannerCameraState = {
  longitude: number
  latitude: number
  zoom: number
  bearing: number
  pitch: number
}

export type CameraForBoundsResult = {
  center?: unknown
  zoom?: number
  bearing?: number
  pitch?: number
} | null

function readLngLat(center: unknown): { longitude: number; latitude: number } | null {
  if (Array.isArray(center) && center.length >= 2) {
    const longitude = Number(center[0])
    const latitude = Number(center[1])
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null
    return { longitude, latitude }
  }
  if (center && typeof center === 'object') {
    const record = center as { lng?: unknown; lon?: unknown; lat?: unknown }
    const longitude = Number(record.lng ?? record.lon)
    const latitude = Number(record.lat)
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null
    return { longitude, latitude }
  }
  return null
}

export function cameraStateFromBounds(
  camera: CameraForBoundsResult,
  fallbackBearing = 0,
  fallbackPitch = 0
): PlannerCameraState | null {
  if (!camera || camera.zoom === undefined) return null
  const center = readLngLat(camera.center)
  if (!center || !Number.isFinite(camera.zoom)) return null
  return {
    longitude: center.longitude,
    latitude: center.latitude,
    zoom: camera.zoom,
    bearing: camera.bearing ?? fallbackBearing,
    pitch: camera.pitch ?? fallbackPitch,
  }
}

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * t
}

export function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3)
}

export function interpolateCamera(from: PlannerCameraState, to: PlannerCameraState, t: number): PlannerCameraState {
  const e = easeOutCubic(Math.min(1, Math.max(0, t)))
  return {
    longitude: lerp(from.longitude, to.longitude, e),
    latitude: lerp(from.latitude, to.latitude, e),
    zoom: lerp(from.zoom, to.zoom, e),
    bearing: lerp(from.bearing, to.bearing, e),
    pitch: lerp(from.pitch, to.pitch, e),
  }
}

export const FIT_MIN_VISIBLE_PAN_PX = 48
export const FIT_MIN_VISIBLE_ZOOM_DELTA = 0.25
export const FIT_ZOOM_DIP = 0.6

export function cameraPanDeltaPx(from: PlannerCameraState, to: PlannerCameraState): number {
  const zoom = Math.min(from.zoom, to.zoom)
  const worldSize = 512 * Math.pow(2, zoom)
  const latRad = (((from.latitude + to.latitude) / 2) * Math.PI) / 180
  const dx = ((to.longitude - from.longitude) / 360) * worldSize
  const dy = (((to.latitude - from.latitude) / 360) * worldSize) / Math.max(Math.cos(latRad), 0.01)
  return Math.hypot(dx, dy)
}

export function isSubtleCameraChange(from: PlannerCameraState, to: PlannerCameraState): boolean {
  return (
    cameraPanDeltaPx(from, to) < FIT_MIN_VISIBLE_PAN_PX &&
    Math.abs(to.zoom - from.zoom) < FIT_MIN_VISIBLE_ZOOM_DELTA &&
    Math.abs(to.bearing - from.bearing) < 1 &&
    Math.abs(to.pitch - from.pitch) < 1
  )
}

export type RouteFitMotion = { kind: 'fitBounds' } | { kind: 'zoomDip'; midZoom: number; final: PlannerCameraState }

export function planRouteFitMotion(from: PlannerCameraState, to: PlannerCameraState): RouteFitMotion {
  if (!isSubtleCameraChange(from, to)) return { kind: 'fitBounds' }
  return {
    kind: 'zoomDip',
    midZoom: Math.max(to.zoom - FIT_ZOOM_DIP, 0),
    final: to,
  }
}
